import { DmnModel, HitPolicy } from '../types';

export const INITIAL_DMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="https://www.omg.org/spec/DMN/20191111/MODEL/" xmlns:dmndi="https://www.omg.org/spec/DMN/20191111/DMNDI/" xmlns:dc="http://www.omg.org/spec/DMN/20180521/DC/" id="definitions_1" name="definitions" namespace="http://camunda.org/schema/1.0/dmn">
  <decision id="decision_1" name="Employee Onboarding Eligibility">
    <decisionTable id="decisionTable_1" hitPolicy="UNIQUE">
      <input id="c1" label="Employee Type">
        <inputExpression id="inputExpression_1" typeRef="string">
          <text>empType</text>
        </inputExpression>
      </input>
      <input id="c2" label="Experience (Years)">
        <inputExpression id="inputExpression_2" typeRef="number">
          <text>expYears</text>
        </inputExpression>
      </input>
      <output id="c3" label="Eligible" name="isEligible" typeRef="boolean"/>
      <output id="c4" label="Bonus %" name="bonusPercent" typeRef="number"/>
      <rule id="r1">
        <inputEntry id="ue_1_1"><text>"Full Time"</text></inputEntry>
        <inputEntry id="ue_1_2"><text>&gt; 5</text></inputEntry>
        <outputEntry id="le_1_3"><text>true</text></outputEntry>
        <outputEntry id="le_1_4"><text>10</text></outputEntry>
      </rule>
      <rule id="r2">
        <inputEntry id="ue_2_1"><text>"Full Time"</text></inputEntry>
        <inputEntry id="ue_2_2"><text>&lt;= 5</text></inputEntry>
        <outputEntry id="le_2_3"><text>true</text></outputEntry>
        <outputEntry id="le_2_4"><text>5</text></outputEntry>
      </rule>
      <rule id="r3">
        <inputEntry id="ue_3_1"><text>"Contractor"</text></inputEntry>
        <inputEntry id="ue_3_2"><text>-</text></inputEntry>
        <outputEntry id="le_3_3"><text>false</text></outputEntry>
        <outputEntry id="le_3_4"><text>0</text></outputEntry>
      </rule>
      <rule id="r4">
        <inputEntry id="ue_4_1"><text>"Intern"</text></inputEntry>
        <inputEntry id="ue_4_2"><text>-</text></inputEntry>
        <outputEntry id="le_4_3"><text>true</text></outputEntry>
        <outputEntry id="le_4_4"><text>2</text></outputEntry>
      </rule>
    </decisionTable>
  </decision>
</definitions>`;

export const parseDmnXml = (xmlStr: string): DmnModel => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlStr, "text/xml");

  const definitions = xmlDoc.getElementsByTagName("definitions")[0];
  if (!definitions) throw new Error("Invalid DMN: No definitions found");

  // Try to find a decision with a table
  const decisions = Array.from(xmlDoc.getElementsByTagName("decision"));
  const decision = decisions.find(d => d.getElementsByTagName("decisionTable").length > 0);
  
  if (!decision) throw new Error("Invalid DMN: No decision with a table found");

  const decisionTable = decision.getElementsByTagName("decisionTable")[0];

  const model: DmnModel = {
    id: decision.getAttribute("id") || `d_${Date.now()}`,
    name: decision.getAttribute("name") || "Decision",
    hitPolicy: (decisionTable.getAttribute("hitPolicy") as HitPolicy) || "UNIQUE",
    columns: [],
    rules: []
  };

  // Parse Inputs
  // Note: DMN 1.3 inputs are direct children of decisionTable
  const inputElements = Array.from(decisionTable.children).filter(el => el.tagName === 'input');
  inputElements.forEach((input) => {
    const inputExpression = input.getElementsByTagName("inputExpression")[0];
    const textNode = inputExpression?.getElementsByTagName("text")[0];
    
    model.columns.push({
      id: input.getAttribute("id") || `c_${Math.random().toString(36).substr(2, 9)}`,
      label: input.getAttribute("label") || "Input",
      variableName: textNode?.textContent || "",
      type: 'input',
      dataType: (inputExpression?.getAttribute("typeRef") as any) || 'string'
    });
  });

  // Parse Outputs
  const outputElements = Array.from(decisionTable.children).filter(el => el.tagName === 'output');
  outputElements.forEach((output) => {
    model.columns.push({
      id: output.getAttribute("id") || `c_${Math.random().toString(36).substr(2, 9)}`,
      label: output.getAttribute("label") || "Output",
      variableName: output.getAttribute("name") || "",
      type: 'output',
      dataType: (output.getAttribute("typeRef") as any) || 'string'
    });
  });

  // Parse Rules
  const ruleElements = Array.from(decisionTable.children).filter(el => el.tagName === 'rule');
  ruleElements.forEach((rule) => {
    const ruleId = rule.getAttribute("id") || `r_${Math.random().toString(36).substr(2, 9)}`;
    const cells: Record<string, string> = {};
    
    const inputEntries = Array.from(rule.children).filter(el => el.tagName === 'inputEntry');
    const outputEntries = Array.from(rule.children).filter(el => el.tagName === 'outputEntry');

    const inputCols = model.columns.filter(c => c.type === 'input');
    inputCols.forEach((col, idx) => {
       const entry = inputEntries[idx];
       const text = entry?.getElementsByTagName("text")[0]?.textContent;
       cells[col.id] = text || "";
    });

    const outputCols = model.columns.filter(c => c.type === 'output');
    outputCols.forEach((col, idx) => {
        const entry = outputEntries[idx];
        const text = entry?.getElementsByTagName("text")[0]?.textContent;
        cells[col.id] = text || "";
    });

    model.rules.push({ id: ruleId, cells });
  });

  return model;
};

export const generateDmnXml = (model: DmnModel): string => {
  const inputCols = model.columns.filter(c => c.type === 'input');
  const outputCols = model.columns.filter(c => c.type === 'output');

  const escape = (str: string | undefined) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');

  const inputsXml = inputCols.map(col => `
      <input id="${col.id}" label="${escape(col.label)}">
        <inputExpression id="exp_${col.id}" typeRef="${col.dataType}">
          <text>${escape(col.variableName)}</text>
        </inputExpression>
      </input>`).join('');

  const outputsXml = outputCols.map(col => `
      <output id="${col.id}" label="${escape(col.label)}" name="${escape(col.variableName)}" typeRef="${col.dataType}"/>`).join('');

  const rulesXml = model.rules.map(rule => `
      <rule id="${rule.id}">
        ${inputCols.map((col, idx) => `
        <inputEntry id="in_${rule.id}_${idx}">
          <text>${escape(rule.cells[col.id])}</text>
        </inputEntry>`).join('')}
        ${outputCols.map((col, idx) => `
        <outputEntry id="out_${rule.id}_${idx}">
          <text>${escape(rule.cells[col.id])}</text>
        </outputEntry>`).join('')}
      </rule>`).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="https://www.omg.org/spec/DMN/20191111/MODEL/" xmlns:dmndi="https://www.omg.org/spec/DMN/20191111/DMNDI/" xmlns:dc="http://www.omg.org/spec/DMN/20180521/DC/" id="definitions_${Date.now()}" name="definitions" namespace="http://camunda.org/schema/1.0/dmn">
  <decision id="${model.id || 'decision_1'}" name="${escape(model.name)}">
    <decisionTable id="table_${model.id || '1'}" hitPolicy="${model.hitPolicy}">
      ${inputsXml}
      ${outputsXml}
      ${rulesXml}
    </decisionTable>
  </decision>
</definitions>`;
};
